'use client';

import { useContext, useState } from 'react';
import type { AffiliateCardParamsBase, ProductCardTab } from '@/app_components/AffiliateProductCard/types';
import { DescriptionParser } from '@/app_lib/htmlParser/DescriptionParser';
import { options } from '@/app_lib/htmlParser/parsers/parserOptions';
import { GtmContext } from '@/app_lib/providers/GtmProvider';
import { trackEvent } from '@/app_lib/services/Gtm/utils';
import { ContentPlacement } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';

const MAX_WIDTH = 100; // For a Tab Name

export const ProductCardTabs = ({
  tabs,
  affiliateCardParamsBase,
}: {
  tabs: ProductCardTab[];
  affiliateCardParamsBase: AffiliateCardParamsBase;
}) => {
  const { pageData } = useContext(GtmContext);
  const [currentTab, setCurrentTab] = useState<number>(0);
  if (tabs.length === 0) {
    return null;
  }

  return (
    <div
      className='flex flex-col gap-lg'
      data-cy={ContentPlacement.AffiliateProductCardTabs}
      style={{
        ['--tab-max-width' as string]: `${MAX_WIDTH / tabs.length}%`,
      }}
    >
      <div className='relative min-w-0'>
        <div className='absolute inset-x-0 bottom-0 border-b stroke-secondary' />
        <div className='flex min-w-0 items-end overflow-x-auto scrollbar-hidden'>
          {tabs.map((tab, index) => (
            <button
              aria-label={`View tab ${index + 1}`}
              className={cn(
                'z-10 shrink-0 cursor-pointer border-b px-sm py-xxs font-graphik-compact typography-default font-medium md:max-w-[var(--tab-max-width)]',
                currentTab === index ? 'border-accent text-accent' : 'text-secondary border-transparent',
              )}
              key={tab.name}
              onClick={() => {
                if (index !== currentTab) {
                  trackEvent({
                    ...pageData,
                    affiliateCardParams: {
                      ...affiliateCardParamsBase,
                      clickTarget: 'Tab Button',
                    },
                    eventAction: 'affiliate_card_click',
                  });
                }
                setCurrentTab(index);
              }}
              type='button'
            >
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      {tabs.map((tab, index) => (
        <div
          className={cn(index !== currentTab && 'hidden', index === currentTab && 'contents')}
          key={`${tab.name} tab content`}
        >
          <DescriptionParser
            className='
							[&_*]:font-graphik-compact [&_p]:typography-sm [&_p]:text-secondary [&_p_strong]:font-graphik 
							[&_p_strong]:typography-xs [&_strong]:text-primary [&_strong]:font-medium 
							[&_a]:text-accent [&_a]:font-medium [&_a]:hover:underline [&_a]:disabled:hover:no-underline [&_a]:aria-disabled:hover:no-underline 
							[&_ul]:list-outside [&_ul]:flex [&_ul]:flex-col [&_ul]:pl-sm [&_ul]:gap-sm [&_ul]:list-disc  
							[&_ol]:list-outside [&_ol]:marker:typography-md [&_ol]:flex [&_ol]:flex-col [&_ol]:gap-sm [&_ol]:pl-xs [&_ol]:list-decimal
							[&_li]:typography-sm [&_li]:text-secondary [&_li_ul]:pl-xs [&_li_ol]:pl-xs [&_li_ul]:pt-xxs [&_li_ol]:pt-xxs'
            content={tab.content}
            options={options}
          />
        </div>
      ))}
    </div>
  );
};
